import { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Icon } from '@capsico/ui';
import {
  QUICK_REPLIES,
  type BillLine,
  type CustomerOrderStage,
  type OfferFunder,
  type QuickReply,
} from '@capsico/contracts';
import { useI18n } from '../i18n';
import { useMyOrder } from '../hooks/useMyOrders';
import { useOrderPayment } from '../hooks/useOrderPayment';
import { useLiveOrder } from '../hooks/useLiveOrder';
import { useOrderChat } from '../hooks/useOrderChat';
import { chatBubbles, useTrackView } from '../hooks/useTrackView';
import { useDeliveryCode, useNow, useRiderCall } from '../hooks/useTrackExtras';
import { useOrderRating } from '../hooks/useReviews';
import { OrderRatingCard, StarRow } from '../components/Reviews';
import { RateFlow } from './RateFlow';
import {
  ChatSheet,
  DeliveryCodeCard,
  LiveBanner,
  MapSkeleton,
  ProofPhotoCard,
  RiderCard,
  TrackStatusCard,
  TrackStepper,
} from '../components/Tracking';
import { refundRowView } from '../i18n/labels';
import { money } from '../utils/money';
import { dayMonth, etaText, orderTime } from '../utils/format';
import { BillCard, WhySheet } from '../components/Checkout';
import { CashbackBanner } from '../components/Growth';
import { useOrderCashback } from '../hooks/useCashback';
import {
  ConfirmSheet,
  InvoicesCard,
  MethodPickerSheet,
  PaymentPendingCard,
  QrSheet,
  RefundsCard,
} from '../components/Payment';
import {
  Button,
  Card,
  EmptyState,
  IconButton,
  ListSkeleton,
  Notice,
  PageHeader,
  VegMark,
} from '../components/ui';

const STEPS: CustomerOrderStage[] = ['placed', 'preparing', 'ready', 'on_the_way', 'delivered'];
const QUICK_KEYS = Object.keys(QUICK_REPLIES) as QuickReply[];

// MapLibre is heavy: it loads only when a live map is actually on screen.
const LiveMap = lazy(() => import('../components/LiveMap'));

/**
 * Order page (C19 + W11 + W14): the "Order placed! 🎉" hero right after checkout, or — for online
 * orders still waiting — the payment card (countdown, Pay now, Pay another way, Cancel); then the
 * live tracking block (C21–C25: map, status + ETA, stepper, rider card with masked call and chat,
 * delivery code, late apology, door photo) — or, when tracking is off on the server, the plain
 * stage ladder — then the payment line, refunds, tax invoices, items, address and the bill.
 */
export function OrderPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { orderId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const justPlaced = params.get('placed') === '1';
  const live = useLiveOrder(orderId);
  const o = useMyOrder(orderId, { poll: !live.available });
  const [why, setWhy] = useState<BillLine | null>(null);
  const now = useNow(15_000, live.moving);
  const view = useTrackView(live.track, now, t);
  const quickReplyText = useMemo(
    () =>
      Object.fromEntries(QUICK_KEYS.map((key) => [key, t(`chat.quick.${key}`)])) as Record<
        QuickReply,
        string
      >,
    [t],
  );
  const chat = useOrderChat(orderId, view?.chatOpen ?? false, {
    failed: t('chat.failed'),
    quickReplies: quickReplyText,
  });
  const code = useDeliveryCode(orderId, view?.showCode ?? false);
  const rider = useRiderCall(orderId, { unavailable: t('track.callUnavailable') });
  // W17: rate after delivery — the sheet opens by itself once per order, then from the card.
  const rating = useOrderRating(orderId, o.order?.stage === 'delivered');
  // W18: Capsico Money cashback this delivered order earned (null when none / growth off).
  const cashback = useOrderCashback(orderId, o.order?.stage === 'delivered');
  const dropPayParam = useCallback(() => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('pay');
        return next;
      },
      { replace: true },
    );
  }, [setParams]);
  const p = useOrderPayment(
    o.order,
    { autoStart: params.get('pay') === '1', onAutoStarted: dropPayParam },
    {
      paid: t('payment.paid'),
      notFinished: t('payment.notFinished'),
      notYet: t('payment.notYet'),
      failed: t('payment.failed'),
      cancelled: t('payment.cancelled'),
      copied: t('payment.qrCopied'),
      gatewayDown: t('payment.gatewayDown'),
      refundSaved: t('refund.saved'),
      invoiceFailed: t('invoice.failed'),
    },
  );

  const header = (
    <PageHeader
      title={o.order ? t('orders.number', { number: o.order.number }) : t('orders.title')}
      onBack={() => (justPlaced ? navigate('/', { replace: true }) : navigate(-1))}
      backLabel={t('common.back')}
      action={
        <IconButton
          icon="info"
          label={t('help.orderHelp')}
          onClick={() => navigate(`/account/help/new?orderId=${orderId}`)}
        />
      }
    />
  );

  if (o.loading) {
    return (
      <div className="min-h-dvh bg-page">
        {header}
        <div className="px-4 pt-5">
          <ListSkeleton rows={4} />
        </div>
      </div>
    );
  }
  if (!o.order) {
    return (
      <div className="min-h-dvh bg-page">
        {header}
        {o.notFound ? (
          <EmptyState icon="receipt" title={t('orders.notFound')} />
        ) : (
          <div className="px-4 pt-5">
            <Notice
              tone="negative"
              supportRef
              title={t('common.wrong')}
              action={
                <Button size="sm" tone="outline" onClick={o.retry}>
                  {t('common.retry')}
                </Button>
              }
            />
          </div>
        )}
      </div>
    );
  }

  const order = o.order;
  const country = order.country;
  const stepIndex = STEPS.indexOf(order.stage);
  const funding = (value: OfferFunder) => t(`checkout.funding.${value}`);
  const payment = order.payment;
  const waiting = order.stage === 'awaiting_payment';
  // Live tracking takes over once the order is with the restaurant (not while it waits for
  // payment or for its scheduled time) and only when the server has tracking switched on.
  const showLive = live.available && !waiting && order.stage !== 'scheduled';
  const methodRows = payment.retryMethods.map((method) => ({
    method,
    name: t(`pay.method.${method}`),
    available: true,
    caption: null,
  }));
  const refundRows = payment.refunds.map((refund) => refundRowView(t, country, refund));
  const paidLine =
    payment.status === 'cod_due'
      ? t('orders.payCash', { amount: money(country, payment.payableMinor) })
      : payment.status === 'refunded'
        ? t('orders.refunded')
        : payment.status === 'partly_refunded'
          ? t('payment.partlyRefunded', { amount: money(country, payment.refundedMinor) })
          : payment.status === 'failed'
            ? t('payment.failedStatus')
            : t('orders.paid');

  return (
    <div className="min-h-dvh bg-page pb-10">
      {header}
      <div className="flex flex-col gap-5 px-4 pt-4">
        {justPlaced && !waiting && order.stage !== 'cancelled' && (
          <div className="rounded-sheet bg-linear-to-br from-brand-pale to-brand px-5 py-6 text-center text-on-brand">
            <span className="mx-auto inline-flex size-14 items-center justify-center rounded-full bg-surface text-positive">
              <Icon name="check" size={28} />
            </span>
            <h1 className="mt-3 text-headline font-bold">{t('orders.placedTitle')}</h1>
            <p className="mt-1 text-sm">{t('orders.placedBody', { name: order.outlet.name })}</p>
          </div>
        )}

        {waiting && (
          <PaymentPendingCard
            title={t('payment.waitingTitle')}
            body={t('payment.waitingBody', {
              amount: money(country, payment.intent?.amountMinor ?? payment.payableMinor),
            })}
            timer={p.timer}
            expired={p.expired}
            labels={{
              payNow: t('payment.payNow'),
              another: t('payment.payAnother'),
              cancel: t('payment.cancelOrder'),
            }}
            busy={p.busy}
            canResume={p.canResume}
            canSwitch={p.canSwitch}
            cancelling={p.abandoning}
            onPayNow={p.resume}
            onAnother={p.openPicker}
            onCancel={p.askCancel}
          />
        )}

        {showLive && view ? (
          <>
            {live.reconnecting && <LiveBanner text={t('track.reconnecting')} />}
            {view.showMap && (
              <Suspense fallback={<MapSkeleton />}>
                <LiveMap
                  restaurant={view.map.restaurant}
                  drop={view.map.drop}
                  rider={view.map.rider}
                  labels={{
                    map: t('track.mapLabel'),
                    restaurant: t('track.pinRestaurant'),
                    drop: t('track.pinHome'),
                    rider: t('track.pinRider'),
                    recenter: t('track.recenter'),
                  }}
                />
              </Suspense>
            )}
            <TrackStatusCard
              title={view.title}
              subtitle={view.subtitle}
              etaCaption={view.etaCaption}
              etaValue={view.etaValue}
              late={view.late}
            >
              {view.late && view.moving && (
                <p className="mt-3 rounded-xl bg-warning-bg px-3 py-2 text-xs text-ink">
                  {t('track.lateBody')}
                </p>
              )}
              {view.cancelled && order.cancelledReason && (
                <p className="mt-2 text-xs font-medium text-negative">
                  {t('orders.cancelledBecause', { reason: order.cancelledReason })}
                </p>
              )}
              <TrackStepper steps={view.steps} label={t('track.steps')} />
            </TrackStatusCard>
            {view.showRider && view.rider && (
              <RiderCard
                name={view.rider.name}
                photoUrl={view.rider.photoUrl}
                ratingText={view.rider.ratingText}
                vehicleText={view.rider.vehicleText}
                note={t('track.rider.masked')}
                calling={rider.calling}
                chatEnabled={view.chatOpen}
                unread={chat.unread}
                onCall={rider.call}
                onChat={chat.openSheet}
                labels={{
                  call: t('track.rider.call'),
                  chat: t('track.rider.chat'),
                  unread: t('track.rider.unread', { count: chat.unread }),
                }}
              />
            )}
            {view.showCode && (
              <DeliveryCodeCard
                title={t('track.code.title')}
                code={code.view?.code ?? null}
                hint={t('track.code.hint')}
                lockedText={code.view?.locked ? t('track.code.locked') : null}
                loading={code.loading}
                failedText={code.failed ? t('track.code.failed') : null}
                onRetry={code.retry}
                retryLabel={t('common.retry')}
              />
            )}
            {view.showLeaveAtDoor && <Notice tone="brand" title={t('track.leaveAtDoor')} />}
            {view.lateApology && <Notice tone="positive" title={t('track.lateApology')} />}
            {view.showProof && view.proofPhotoUrl && (
              <ProofPhotoCard
                title={t('track.proof.title')}
                caption={t('track.proof.caption')}
                url={view.proofPhotoUrl}
              />
            )}
          </>
        ) : (
          <Card>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-base font-bold text-ink">{t(`stage.${order.stage}`)}</p>
                <p className="mt-0.5 text-xs text-ink-2">
                  {order.outlet.name} · {order.outlet.locality}
                </p>
                <p className="mt-0.5 text-caption text-ink-3">{orderTime(order.placedAt)}</p>
              </div>
              {order.eta && order.stage !== 'delivered' && order.stage !== 'cancelled' && (
                <span className="shrink-0 rounded-lg bg-brand-soft px-2.5 py-1 text-xs font-semibold text-ink tabular">
                  {t('orders.eta', { eta: etaText(order.eta) })}
                </span>
              )}
            </div>
            {order.scheduledFor && order.stage === 'scheduled' && (
              <p className="mt-2 text-xs text-ink-2">
                {t('orders.scheduledFor', { time: orderTime(order.scheduledFor) })}
              </p>
            )}
            {waiting ? null : order.stage === 'cancelled' ? (
              order.cancelledReason && (
                <p className="mt-3 text-xs font-medium text-negative">
                  {t('orders.cancelledBecause', { reason: order.cancelledReason })}
                </p>
              )
            ) : (
              <ol className="mt-4 flex items-center gap-1.5" aria-label={t(`stage.${order.stage}`)}>
                {STEPS.map((step, index) => (
                  <li
                    key={step}
                    className={`h-1.5 flex-1 rounded-full ${index <= stepIndex ? 'bg-brand-600' : 'bg-tile'}`}
                    title={t(`stage.${step}`)}
                  />
                ))}
              </ol>
            )}
          </Card>
        )}

        {rating.pending && !rating.review && (
          <OrderRatingCard
            title={t('rate.cardTitle')}
            body={`${t('rate.cardBody', { name: order.outlet.name })} · ${t('rate.rateBy', { time: orderTime(rating.pending.rateBy) })}`}
            actionLabel={t('rate.cardAction')}
            onAction={rating.openRate}
            stars={null}
          />
        )}
        {rating.review && (
          <OrderRatingCard
            title={t('rate.yours')}
            body={
              rating.editable
                ? t('rate.editUntil', { time: orderTime(rating.review.editableUntil) })
                : order.outlet.name
            }
            actionLabel={rating.editable ? t('common.edit') : null}
            onAction={rating.openEdit}
            stars={
              <span className="flex flex-wrap items-center gap-x-4 gap-y-1 text-caption text-ink-2">
                <span className="inline-flex items-center gap-1.5">
                  {t('rate.foodShort')}
                  <StarRow
                    value={rating.review.foodStars}
                    label={t('rate.starAria', { n: rating.review.foodStars })}
                    size={12}
                  />
                </span>
                {rating.review.deliveryStars !== null && (
                  <span className="inline-flex items-center gap-1.5">
                    {t('rate.deliveryShort')}
                    <StarRow
                      value={rating.review.deliveryStars}
                      label={t('rate.starAria', { n: rating.review.deliveryStars })}
                      size={12}
                    />
                  </span>
                )}
              </span>
            }
          />
        )}

        {!waiting && (
          <Notice
            tone={
              payment.status === 'cod_due'
                ? 'brand'
                : payment.status === 'failed'
                  ? 'negative'
                  : 'positive'
            }
            title={paidLine}
          >
            {payment.walletMinor > 0
              ? t('payment.walletUsed', { amount: money(country, payment.walletMinor) })
              : null}
          </Notice>
        )}

        {refundRows.length > 0 && (
          <RefundsCard
            title={t('refund.title')}
            rows={refundRows}
            busyId={p.choosingRefundId}
            onChoose={p.chooseRefund}
          />
        )}

        {payment.invoices.length > 0 && (
          <InvoicesCard
            title={t('invoice.title')}
            downloadLabel={t('invoice.download')}
            downloading={p.downloadingInvoice}
            onDownload={p.downloadInvoice}
            rows={payment.invoices.map((invoice) => ({
              id: invoice.id,
              kind: invoice.kind,
              title: t(`invoice.${invoice.kind}`),
              subtitle: `${invoice.number} · ${orderTime(invoice.issuedAt)}`,
              amount: money(country, invoice.totalMinor),
            }))}
          />
        )}

        <Card>
          <h2 className="text-sm font-bold text-ink">{t('orders.items')}</h2>
          <div className="mt-2 divide-y divide-hair">
            {order.lines.map((line, index) => (
              <div key={index} className="flex items-start gap-3 py-2.5">
                <span className="pt-1">
                  <VegMark type={line.foodType} size={12} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-ink">
                    {line.quantity} × {line.name}
                  </p>
                  {(line.variantName || line.addons.length > 0) && (
                    <p className="text-caption text-ink-3">
                      {[line.variantName, ...line.addons.map((addon) => addon.name)]
                        .filter(Boolean)
                        .join(', ')}
                    </p>
                  )}
                </div>
                <span className="text-sm text-ink tabular">
                  {money(country, line.lineTotalMinor)}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 shadow-[inset_0_1px_0_var(--color-hair)]">
            <p className="text-caption text-ink-3">{t('orders.deliveringTo')}</p>
            <p className="text-sm font-semibold text-ink">{order.address.label}</p>
            <p className="text-xs text-ink-2">{order.address.line}</p>
          </div>
        </Card>

        <BillCard
          title={t('checkout.bill')}
          lines={order.bill.lines}
          total={order.bill.totalMinor}
          totalLabel={t('checkout.toPay')}
          savings={
            order.bill.savingsMinor > 0
              ? t('checkout.savedTotal', { amount: money(country, order.bill.savingsMinor) })
              : null
          }
          country={country}
          fundingLabel={funding}
          whyLabel={t('checkout.why')}
          onWhy={setWhy}
        />

        {cashback && cashback.amountMinor > 0 && (
          <CashbackBanner
            title={t('cashback.orderEarned', { amount: money(country, cashback.amountMinor) })}
            body={
              cashback.expiresAt
                ? t('cashback.expires', { date: dayMonth(cashback.expiresAt) })
                : null
            }
          />
        )}

        <Button
          tone="outline"
          full
          onClick={() => navigate(`/account/help/new?orderId=${order.id}`)}
        >
          <Icon name="info" size={18} />
          {t('help.orderHelp')}
        </Button>

        <div className="grid grid-cols-2 gap-3">
          <Button tone="outline" onClick={() => navigate('/account/orders')}>
            {t('orders.title')}
          </Button>
          <Button onClick={() => navigate('/', { replace: true })}>{t('orders.backHome')}</Button>
        </div>
      </div>
      {why && <WhySheet line={why} closeLabel={t('checkout.gotIt')} onClose={() => setWhy(null)} />}
      {chat.sheetOpen && (
        <ChatSheet
          title={t('chat.title', { name: view?.rider?.name ?? t('track.pinRider') })}
          bubbles={chatBubbles(chat.messages)}
          loading={chat.loading}
          open={chat.open}
          closedText={t('chat.closed')}
          emptyText={t('chat.empty')}
          quickReplies={chat.quickReplies.map((key) => ({ key, label: quickReplyText[key] }))}
          text={chat.text}
          maxLength={chat.maxLength}
          canSend={chat.canSend}
          draftRestored={chat.draftRestored}
          labels={{
            placeholder: t('chat.placeholder'),
            send: t('chat.send'),
            sending: t('chat.sending'),
            draftRestored: t('common.draftRestored'),
            keep: t('common.keep'),
            discard: t('common.discard'),
          }}
          onText={chat.setText}
          onSend={chat.sendText}
          onQuick={(key) => chat.sendQuick(key as QuickReply)}
          onKeepDraft={chat.keepDraft}
          onDiscardDraft={chat.discardDraft}
          onClose={chat.closeSheet}
        />
      )}
      {rating.target && (
        <RateFlow
          key={
            rating.target.kind === 'edit' ? rating.target.review.id : rating.target.pending.orderId
          }
          target={rating.target}
          onClose={rating.close}
        />
      )}
      {p.pickerOpen && (
        <MethodPickerSheet
          title={t('payment.pickMethod')}
          rows={methodRows}
          busyMethod={p.retryingMethod}
          onPick={p.retry}
          onClose={p.closePicker}
        />
      )}
      {p.qr && (
        <QrSheet
          title={t('payment.qrTitle', { amount: money(country, p.qr.amountMinor) })}
          body={t('payment.qrBody')}
          payload={p.qr.payload}
          labels={{
            copy: t('payment.qrCopy'),
            waiting: t('payment.qrWaiting'),
            paid: t('payment.qrPaid'),
          }}
          checking={p.checking}
          onCopy={p.copyQr}
          onPaid={p.checkQr}
          onClose={p.closeQr}
        />
      )}
      {p.confirmCancel && (
        <ConfirmSheet
          title={t('payment.cancelTitle')}
          body={t('payment.cancelBody')}
          yesLabel={t('payment.cancelOrder')}
          noLabel={t('payment.keepOrder')}
          busy={p.abandoning}
          onYes={p.abandon}
          onNo={p.keepOrder}
        />
      )}
    </div>
  );
}
